import { createTheme } from "@mui/material/styles";
import "@fontsource/poppins";
import "@fontsource/poppins/400.css";
import "@fontsource/poppins/400-italic.css";

const theme = createTheme({
  palette: {
    primary: {
      main: "#1DA7A7",
      dark: "#115293",
    },
    secondary: {
      main: "#1A2130",
    },
  },
  typography: {
    fontFamily: "Poppins",
  },
  components: {
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: "16px",
          boxShadow: "none !important",
          backgroundImage: "none",
        },
      },
    },

    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: "16px",
          boxShadow: "none !important",
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          borderRadius: 2,

          "&.MuiOutlinedInput-root": {
            borderRadius: "8px",
            minHeight: "40px",

            "& .MuiOutlinedInput-input": {
              padding: "8px 14px",
              height: "auto",
            },

            "&.MuiInputBase-multiline": {
              minHeight: "auto",
              maxHeight: "none",
              height: "auto",

              "& .MuiInputBase-inputMultiline": {
                padding: "8px 14px",
              },
            },

            "&.MuiAutocomplete-inputRoot": {
              padding: "4px 9px",

              "& .MuiOutlinedInput-input": {
                padding: "6px 0px",
              },
            },
          },
        },

        input: {
          boxSizing: "border-box",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            minHeight: "40px",
            maxHeight: "40px",
            "&.Mui-focused": {
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#1DA7A7",
                borderWidth: "2px",
              },
            },
          },
          "& .MuiOutlinedInput-multiline": {
            minHeight: "auto",
            maxHeight: "none",
            padding: 0,
            "& .MuiOutlinedInput-input": {
              padding: "8px 14px",
            },
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            padding: "4px 9px",
            minHeight: "40px",
            maxHeight: "40px",
            '&.MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
              padding: "4px 9px",
            },
          },
        },
        input: {
          padding: "6px 0px !important",
        },
        endAdornment: {
          right: "9px",
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          minHeight: "40px",
          maxHeight: "40px",
          height: "auto",
          display: "flex",
          alignItems: "center",
          padding: "8px 14px",
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          "& .MuiInputLabel-outlined": {
            transform: "translate(12px, 8px) scale(1)",
            "&.MuiInputLabel-shrink": {
              transform: "translate(14px, -9px) scale(0.75)",
            },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: "8px",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          fontSize: "1.25rem",
          color: "#1A2130",
          borderBottom: "1px solid #f0f0f0",
          paddingBottom: "16px",
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: "20px 24px",
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: "16px 24px",
          borderTop: "1px solid #f0f0f0",
          gap: "12px",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          minHeight: "40px",
          maxHeight: "40px",
          borderRadius: "8px",
        },
        containedPrimary: {
          border: "1px solid #1DA7A7",
          backgroundColor: "#1DA7A7",
          color: "#FFFFFF",
          fontWeight: 600,
          "&:hover": {
            backgroundColor: "#1A2130",
            border: "1px solid #1A2130",
            color: "#FFFFFF",
            // transform: "translateY(-1px)",
            // boxShadow: "0 4px 12px rgba(29, 167, 167, 0.3)",
          },
          "&.Mui-disabled": {
            border: "1px solid #D8D9DA",
            backgroundColor: "#D8D9DA",
            color: "#1A2130",
            opacity: 0.7,
          },
        },
        containedSecondary: {
          border: "1px solid #1DA7A7",
          backgroundColor: "#1DA7A7",
          color: "#FFFFFF",
          fontWeight: 600,
          "&:hover": {
            backgroundColor: "#1A2130",
            border: "1px solid #1A2130",
            color: "#FFFFFF",
            transform: "translateY(-1px)",
            boxShadow: "0 4px 12px rgba(29, 167, 167, 0.3)",
          },
          "&.Mui-disabled": {
            border: "1px solid #EFEFEF",
            backgroundColor: "rgba(239, 239, 239, 0.5)",
            color: "#767676",
            opacity: 0.5,
          },
        },
        outlinedPrimary: {
          border: "1px solid #1DA7A7",
          backgroundColor: "transparent",
          color: "#1DA7A7",
          fontWeight: 600,
          "&:hover": {
            backgroundColor: "rgba(29, 167, 167, 0.08)",
            border: "1px solid #1A2130",
            color: "#1A2130",
          },
          "&.Mui-disabled": {
            border: "1px solid #D8D9DA",
            backgroundColor: "transparent",
            color: "#D8D9DA",
            opacity: 0.7,
          },
        },
        outlinedSecondary: {
          border: "1px solid #1A2130",
          backgroundColor: "transparent",
          color: "#1A2130",
          fontWeight: 600,
          "&:hover": {
            backgroundColor: "rgba(26, 33, 48, 0.08)",
            border: "1px solid #1A2130",
            color: "#1A2130",
          },
          "&.Mui-disabled": {
            border: "1px solid #EFEFEF",
            backgroundColor: "transparent",
            color: "#767676",
            opacity: 0.5,
          },
        },
        textPrimary: {
          color: "#1DA7A7",
          fontWeight: 600,
          "&:hover": {
            backgroundColor: "rgba(29, 167, 167, 0.08)",
            color: "#1A2130",
          },
          "&.Mui-disabled": {
            color: "#D8D9DA",
          },
        },
        textSecondary: {
          color: "#1A2130",
          fontWeight: 600,
          "&:hover": {
            backgroundColor: "rgba(26, 33, 48, 0.08)",
          },
          "&.Mui-disabled": {
            color: "#767676",
          },
        },
      },
    },
  },
});

export default theme;
